import React, { useState, useEffect, ReactNode } from 'react'
import { useTableComponentStyle } from 'UI/Table/style'
import { IColumn } from 'typescript/interfaces/tableUi'
import { IBankAccountClient } from 'typescript/interfaces/clients'
import TableComponent, { TableComponentType } from 'UI/Table'
import IconButton from '@material-ui/core/IconButton'
import { ReactComponent as EditIcon } from 'assets/svg/EditIcon.svg'
import { ReactComponent as DeleteIcon } from 'assets/svg/DeleteCircleIcon.svg'
import Checkbox from 'UI/Checkbox'
import FieldVerificationBadge from 'components/FieldVerificationBadge'
import { VerificationFieldKey, VerificationTargetEntity } from 'typescript/enums/documentVerification'

export const TableClientBankAccounts = TableComponent as TableComponentType<IBankAccountClient>

const renderVerifiedValue = (data: IBankAccountClient, value: ReactNode, fieldKey: VerificationFieldKey) => (
  <>
    {value}
    <FieldVerificationBadge targetEntity={VerificationTargetEntity.bankAccount} targetEntityId={data.bankAccount.id} fieldKey={fieldKey} />
  </>
)

interface IUseClientBankAccountsProps {
  handleEdit: (id: number) => void
  handleDelete: (id: number) => void
  handleSetMain: (id?: number | string) => void
  readOnly: boolean
}

export const useClientBankAccounts = ({ handleEdit, handleDelete, handleSetMain, readOnly }: IUseClientBankAccountsProps) => {
  const classes = useTableComponentStyle()
  const [columns, setColumns] = useState<IColumn<IBankAccountClient>[]>([])

  useEffect(() => {
    setColumns([
      {
        label: 'Bank Account #',
        field: 'bankAccount',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => renderVerifiedValue(data, data.bankAccount.bankAccount, VerificationFieldKey.bankAccountNumber),
      },
      {
        label: 'Bank Account Name',
        field: 'bankAccountName',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => renderVerifiedValue(data, data.bankAccount.bankAccountName, VerificationFieldKey.bankAccountName),
      },
      {
        label: 'ACH Bank Routing #',
        field: 'bankRouting',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => renderVerifiedValue(data, data.bankAccount.bankRouting, VerificationFieldKey.bankRoutingNumber),
      },
      {
        label: 'Wire Bank Routing #',
        field: 'bankRoutingWire',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => data.bankAccount.bankRoutingWire,
      },
      {
        label: 'Bank Account Type',
        field: 'bankAccountType',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => data.bankAccount.bankAccountType,
      },
      {
        label: 'Bank Name',
        field: 'bankName',
        bodyCellClassName: classes.secondaryDarkCell,
        render: (data) => renderVerifiedValue(data, data.bankAccount.bankName, VerificationFieldKey.bankName),
      },
      ...(!readOnly
        ? [
            {
              label: 'Is Main',
              field: 'isMain',
              bodyCellClassName: classes.secondaryDarkCell,
              render: (data: IBankAccountClient) => (
                <Checkbox
                  disabled={data.isMain}
                  name="isMain"
                  onChange={(e) => handleSetMain(data.bankAccount.id)}
                  checked={data.isMain}
                  color="primary"
                />
              ),
            },
          ]
        : []),
      ...(!readOnly
        ? [
            {
              field: 'edit',
              label: 'Edit',
              render: (data: IBankAccountClient) => (
                <IconButton size="small" onClick={() => handleEdit(data.bankAccount.id ? +data.bankAccount.id : 0)}>
                  <EditIcon />
                </IconButton>
              ),
            },
          ]
        : []),
      ...(!readOnly
        ? [
            {
              field: 'delete',
              label: 'Delete',
              render: (data: IBankAccountClient) => (
                <IconButton size="small" onClick={() => handleDelete(data.bankAccount.id ? +data.bankAccount.id : 0)}>
                  <DeleteIcon />
                </IconButton>
              ),
            },
          ]
        : []),
    ])
  }, [])

  return {
    columns,
  }
}
